import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { signUp } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, Mail } from "lucide-react";
import flytLogo from "@/assets/flyt-logo.svg";

/** Min 8 tegn, min én liten bokstav, én stor bokstav og ett tall */
function validatePassword(p: string): { valid: boolean; message?: string } {
  if (p.length < 8) return { valid: false, message: "Passordet må ha minst 8 tegn" };
  if (!/[a-z]/.test(p)) return { valid: false, message: "Passordet må inneholde minst én liten bokstav" };
  if (!/[A-Z]/.test(p)) return { valid: false, message: "Passordet må inneholde minst én stor bokstav" };
  if (!/[0-9]/.test(p)) return { valid: false, message: "Passordet må inneholde minst ett tall" };
  return { valid: true };
}

function getPasswordRequirements(p: string) {
  return {
    length: p.length >= 8,
    lowercase: /[a-z]/.test(p),
    uppercase: /[A-Z]/.test(p),
    number: /[0-9]/.test(p),
  };
}

export default function Register() {
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get("plan");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const req = getPasswordRequirements(password);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPasswordError(null);
    setConfirmError(null);
    const pwdCheck = validatePassword(password);
    if (!pwdCheck.valid) {
      setPasswordError(pwdCheck.message ?? "Ugyldig passord");
      toast.error(pwdCheck.message);
      return;
    }
    if (password !== confirmPassword) {
      setConfirmError("Passordene stemmer ikke overens");
      toast.error("Passordene stemmer ikke overens");
      return;
    }
    setLoading(true);
    const redirectParam = searchParams.get("redirect");
    const redirectUrl = redirectParam
      ? `${window.location.origin}${redirectParam}`
      : planParam
        ? `${window.location.origin}/onboarding?plan=${planParam}`
        : window.location.origin;
    const { error } = await signUp(email, password, redirectUrl);
    setLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Registreringen er sendt – sjekk e-posten din.");
      setSent(true);
    }
  }

  if (sent) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md text-center shadow-md rounded-2xl border-primary/10">
          <CardHeader className="space-y-4 pb-2">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <CheckCircle2 className="h-8 w-8" aria-hidden />
            </div>
            <div className="space-y-1">
              <CardTitle className="text-2xl">Registreringen er sendt</CardTitle>
              <CardDescription className="text-base">
                Vi har sendt en bekreftelseslenke til <strong className="text-foreground">{email}</strong>.
              </CardDescription>
            </div>
            <div className="flex items-center justify-center gap-2 rounded-lg bg-muted/50 py-3 px-3 text-sm text-muted-foreground">
              <Mail className="h-4 w-4 shrink-0" />
              <span className="text-center whitespace-nowrap">Klikk på lenken i e-posten for å aktivere kontoen din.</span>
            </div>
          </CardHeader>
          <CardFooter className="flex flex-col gap-3 pt-2">
            <Link
              to="/login"
              className="text-sm font-medium text-primary underline underline-offset-4 hover:no-underline"
            >
              Tilbake til innlogging
            </Link>
            <p className="text-xs text-muted-foreground">
              Kom ikke e-posten? Sjekk søppelpost eller spam-mappen.
            </p>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm shadow-md rounded-2xl">
        <CardHeader className="text-center">
          <img src={flytLogo} alt="Flyt" className="mx-auto mb-2 h-14 w-auto" />
          <CardTitle className="text-2xl">Registrer deg</CardTitle>
          <CardDescription>Opprett en gratis Flyt-konto og kom i gang på under 2 minutter.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="mt-1 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-post</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="Din e-postadresse"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Passord</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(null);
                }}
                required
                autoComplete="new-password"
                minLength={8}
                placeholder="Velg et passord"
                aria-invalid={!!passwordError}
                aria-describedby={passwordError ? "password-error" : "password-requirements"}
              />
              {passwordError && (
                <p id="password-error" className="text-xs text-destructive font-medium" role="alert">
                  {passwordError}
                </p>
              )}
              <ul
                id="password-requirements"
                className="text-xs text-muted-foreground space-y-1"
                aria-live="polite"
              >
                <li className={req.length ? "text-muted-foreground" : "text-destructive"}>
                  {req.length ? "✓" : "○"} Minst 8 tegn
                </li>
                <li className={req.lowercase ? "text-muted-foreground" : "text-destructive"}>
                  {req.lowercase ? "✓" : "○"} Minst én liten bokstav (a–z)
                </li>
                <li className={req.uppercase ? "text-muted-foreground" : "text-destructive"}>
                  {req.uppercase ? "✓" : "○"} Minst én stor bokstav (A–Z)
                </li>
                <li className={req.number ? "text-muted-foreground" : "text-destructive"}>
                  {req.number ? "✓" : "○"} Minst ett tall (0–9)
                </li>
              </ul>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Bekreft passord</Label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setConfirmError(null);
                }}
                required
                autoComplete="new-password"
                minLength={8}
                placeholder="Skriv passordet på nytt"
                aria-invalid={!!confirmError}
              />
              {confirmError && (
                <p className="text-xs text-destructive font-medium" role="alert">
                  {confirmError}
                </p>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Oppretter…" : "Start gratis"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Barnet får sin egen barnemodus. Vi selger ikke data.
            </p>
            <p className="text-sm text-muted-foreground">
              Har du allerede Flyt?{" "}
              <Link to="/login" className="text-primary underline">
                Logg inn
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
